/**
 * TickerTimeframes — "what does every timeframe say about this ticker".
 *
 * The Matcher (/dashboard) answers one window at a time, so comparing DELL at 2w vs 1y
 * means five separate lookups. This page renders all five windows of
 * GET /api/ticker/<ticker>/signals in one view.
 *
 * READOUT FLOOR: there is deliberately NO minimum-similarity floor — the backend returns
 * a row for every ticker it could match at all, including ones built from loose analogs.
 * Weak rows are therefore rendered, not hidden, and carry a visible match-quality meter
 * (`match_quality` + `match_score_median`, both computed server-side) so a loose row
 * READS as loose. Never restore a client-side floor here; fix the indicator instead.
 *
 * TWO DENOMINATORS, deliberately, and they are NAMESPACED. The row's top-level conviction
 * fields (fraction_up, count_total, avg_*) always describe the loose no-floor readout pool,
 * for every ticker. A row that made today's published top list ALSO carries `signal`: the
 * same fields scored over the STRICT match set — the exact analogs /api/featured publishes,
 * so this page and /view-featured agree. Prefer `signal` when it is there, fall back to the
 * base row when it is not, and never print either as a bare "of N": they are unlike numbers
 * over unlike sets. See .claude/rules/full-universe-readout.md (stocks_api).
 *
 * FEATURED FIELDS ARE GATED — `signal` INCLUDED. `featured` / `featured_direction` /
 * `featured_rank` / `signal` are all list-membership facts, which is what /api/featured's
 * anonymous tease protects, so the API withholds them from signed-out callers and sets
 * `featured_gated: true`. They are therefore OPTIONAL here: absent means "unknown", not
 * "not featured" — never render a missing badge, or a missing `signal`, as a negative.
 * Everything else on the page is ungated and reads identically for a featured and a
 * non-featured ticker (that is the point: the base row must not betray membership).
 *
 * NOT SHOWN, by design: any aggregate accuracy / win-rate / hit-rate number. What the
 * analogs did (conviction, counts, average moves) is descriptive per-signal history and
 * is the product; how often the engine is right is not published.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuthUrl } from "../utils/authRedirect";
import {
  Alert, Badge, Button, Card, CardBody, Container, Input, InputGroup, Spinner,
} from "reactstrap";
import { usePostHog } from "@posthog/react";

import requests from "../requests";
import { useSeo } from "../hooks/useSeo";
import { buildDashboardUrl, buildViewFeaturedUrl } from "../utils/links";
import "../style/ticker-timeframes-styles.css";

// Display windows, short→long. Mirrors TICKER_SIGNAL_WINDOWS in resources/ticker.py; the
// response always carries all five string keys, `null` where there is no readout row.
const WINDOWS = [10, 20, 65, 130, 260] as const;

export type MatchQuality = "strong" | "fair" | "weak" | "unknown";

/** The strict (published-signal) conviction block that hangs off a featured row.
 *  Same field names as the base row's readout stats — a different match set, hence the
 *  separate object rather than an in-place substitution. */
interface FeaturedSignal {
  direction: "up" | "down" | null;
  count_total: number;
  fraction_up: number;
  fraction_dn: number;
  avg_up: number;
  avg_dn: number;
}

/** One window's readout row. Mirrors the server-side allowlist in resources/ticker.py.
 *  Only the fields this page renders are declared — the payload carries more. */
export interface WindowReadout {
  window: number;
  window_name: string;
  forecast_window: number;
  signal_date: string | null;
  direction: "up" | "down" | null;

  // Match quality — the readout-floor indicator (see the file header).
  count_analogs: number;
  count_close_matches: number;
  min_score: number;
  match_quality: MatchQuality;
  match_score_median: number | null;
  match_score_max: number | null;
  match_score_min: number | null;

  // Descriptive history. Percentages are 0-100; avg_up/avg_dn are magnitudes
  // (always positive), max_dn is signed negative, avg_all is signed.
  count_total: number;
  count_up: number;
  count_dn: number;
  fraction_up: number;
  fraction_dn: number;
  avg_all: number;
  avg_up: number;
  avg_dn: number;
  max_up: number;
  max_dn: number;
  fraction_delisted: number;

  // Top-list position + the strict published-signal stats — withheld from anonymous
  // callers (see the file header), so these are optional. `undefined` means "not
  // disclosed", which is NOT the same as `false` / "not featured".
  featured?: boolean;
  featured_direction?: "up" | "down" | null;
  featured_rank?: number | null;
  /** The PUBLISHED signal's conviction stats, over the strict match set. Present only on
   *  a featured row, and only for callers allowed to know a row is featured. */
  signal?: FeaturedSignal | null;
}

interface TickerSignalsResponse {
  ticker: string;
  ticker_name: string;
  date: string | null;
  /** True when the caller is signed out and the API withheld every `featured_*` field. */
  featured_gated?: boolean;
  windows: Record<string, WindowReadout | null>;
}

export const QUALITY_LABEL: Record<MatchQuality, string> = {
  strong: "Close match",
  fair: "Fair match",
  weak: "Loose match",
  unknown: "No analogs",
};

// Filled segments in the 3-segment quality meter.
const QUALITY_FILL: Record<MatchQuality, number> = { strong: 3, fair: 2, weak: 1, unknown: 0 };

const QUALITY_HINT: Record<MatchQuality, string> = {
  strong: "The typical analog is as close in shape as the strict pass requires.",
  fair: "The typical analog is near, but short of, the strict bar for this window.",
  weak: "The typical analog is a loose shape match — read this row as a rough sketch.",
  unknown: "The matcher returned no analogs to measure.",
};

const pct = (v: number | null | undefined, digits = 0): string =>
  v === null || v === undefined || !isFinite(v) ? "—" : `${v.toFixed(digits)}%`;

// Signed move, e.g. "+4.2%" / "-3.1%". `sign` forces the display sign for magnitude
// fields (avg_up/avg_dn are stored positive regardless of direction).
const move = (v: number | null | undefined, sign: 1 | -1 = 1): string => {
  if (v === null || v === undefined || !isFinite(v)) return "—";
  const val = sign * Math.abs(v);
  return `${val > 0 ? "+" : ""}${val.toFixed(1)}%`;
};

// Similarity is stored 0-1 (SliceParam.min_score); the Matcher screen shows it 0-100.
const score = (v: number | null | undefined): string =>
  v === null || v === undefined || !isFinite(v) ? "—" : `${(v * 100).toFixed(0)}`;

// A safety margin subtracted from the deep-linked floor, in percentage points. Guards
// against float-rounding drift between the nightly readout's stored score and a fresh
// live query's re-computed one — not a meaningful dilution of the result set.
const MATCHER_DEEPLINK_SCORE_MARGIN = 2;

/** The minScore to deep-link the Matcher with for this row, or undefined when there is
 *  nothing to floor against (match_quality "unknown" — the matcher found zero analogs at
 *  ANY similarity, so no score would help; see the caller, which disables the action
 *  entirely in that case rather than linking to a query that can only fail).
 *
 *  Deliberately NOT the window's generic per-window default (WINDOW_DEFAULTS in
 *  QueryMatcher.tsx — 99/93/80/80/80 by window). That default is a fixed per-WINDOW bar;
 *  this row's own `match_score_min` is the actual weakest analog THIS ticker's THIS window
 *  readout counted, at min_score=0 over the full candidate pool. A live query is the same
 *  retrieval re-run with minScore as a post-hoc floor, so submitting a bar comfortably
 *  below this row's own minimum keeps its analogs in view — rather than the generic
 *  default, which is exactly the bar this ticker/window may have already failed (that is
 *  what a "fair"/"weak" quality label means). This applies uniformly regardless of
 *  match_quality, not just to weak rows: even a "strong" row's own measured floor is a
 *  tighter, more honest bound than a fixed per-window constant, and can never show FEWER
 *  analogs than the generic default would for that ticker.
 */
export function matcherDeepLinkScore(row: WindowReadout): number | undefined {
  if (row.match_score_min === null) return undefined;
  return Math.max(0, Math.round(row.match_score_min * 100) - MATCHER_DEEPLINK_SCORE_MARGIN);
}

// Same number as SLICE_PARAMS' min_matches in daily.py — the production pipeline's own
// "is this even worth featuring" bar, reused here as "how many analogs are worth showing
// at a glance". A weak/fair row's floor sits low enough (by design, see above) that a live
// query at that floor routinely returns the full ~100-analog pool — technically correct,
// but not what "open matcher" implies: a curated look at this row's closest analogs, not
// its entire loose readout pool. A strong row's own floor is already tight, so its natural
// result count doesn't need capping.
const MATCHER_DEEPLINK_MAX_MATCHES = 10;

export function matcherDeepLinkNumMatches(row: WindowReadout): number | undefined {
  return row.match_quality === "weak" || row.match_quality === "fair"
    ? MATCHER_DEEPLINK_MAX_MATCHES
    : undefined;
}

/** Where a row's "open" action goes. A featured row has a published signal, so it opens
 *  the ViewFeatured detail framed in that direction; anything else opens the Matcher at
 *  this window, with a score derived from this row's own measured analogs (see
 *  matcherDeepLinkScore) so the live query doesn't re-apply a generic bar this specific
 *  ticker/window may already be below. Returns null when there is nothing to open (see
 *  matcherDeepLinkScore's "unknown" case) — the caller must not render a link then. */
export function rowHref(ticker: string, row: WindowReadout): string | null {
  // Gated (signed-out) rows have no featured_direction, so they fall through to the
  // Matcher — which is the right destination when we cannot know there is a signal.
  if (row.featured && row.featured_direction) {
    return buildViewFeaturedUrl({
      ticker,
      direction: row.featured_direction,
      window: row.window,
    });
  }
  if (row.match_quality === "unknown") return null;
  return buildDashboardUrl({
    ticker,
    window: row.window,
    score: matcherDeepLinkScore(row),
    numMatches: matcherDeepLinkNumMatches(row),
    forecastWindow: row.forecast_window,
    auto: true,
  });
}

function DirectionBadge({ direction }: { direction: "up" | "down" | null }): React.JSX.Element {
  if (direction === "up") return <Badge className="tf-dir tf-dir-up">Bullish</Badge>;
  if (direction === "down") return <Badge className="tf-dir tf-dir-down">Bearish</Badge>;
  // No side cleared the 60% bar server-side — an honest "no call", not a missing row.
  return <Badge className="tf-dir tf-dir-mixed">No lean</Badge>;
}

function QualityMeter({ quality }: { quality: MatchQuality }): React.JSX.Element {
  const filled = QUALITY_FILL[quality];
  return (
    <span className="tf-meter" aria-hidden="true">
      {[1, 2, 3].map(i => (
        <span key={i} className={`tf-meter-seg${i <= filled ? " is-on" : ""}`} />
      ))}
    </span>
  );
}

// Only used for the empty state, where there is no row to read `window_name` from.
const WINDOW_FALLBACK_NAME: Record<number, string> = {
  10: "2w", 20: "4w", 65: "3m", 130: "6m", 260: "1y",
};

function EmptyRow({ windowKey }: { windowKey: number }): React.JSX.Element {
  return (
    <div className="tf-row tf-row-empty">
      <div className="tf-cell tf-cell-window">
        <span className="tf-window-name">{WINDOW_FALLBACK_NAME[windowKey]}</span>
        <span className="tf-window-sub">{windowKey}-day window</span>
      </div>
      <div className="tf-cell tf-empty-msg">
        No analogs for this window — not enough price history, or the matcher returned nothing.
      </div>
    </div>
  );
}

function ReadoutRow(
  { ticker, row, featuredGated }: { ticker: string; row: WindowReadout; featuredGated: boolean },
): React.JSX.Element {
  const authUrlFor = useAuthUrl();
  const weak = row.match_quality === "weak" || row.match_quality === "unknown";
  const isFeatured = row.featured === true;
  const href = rowHref(ticker, row);
  // Conviction numbers come from the strict published-signal set when the API disclosed
  // one, and from the no-floor readout pool otherwise. A signed-out response carries no
  // `signal` at all, so it always renders the readout pool — correctly, since it is not
  // told whether this window is featured.
  const strict = row.signal ?? null;
  const conviction = strict ?? row;
  const strictStats = strict !== null;
  const classes = [
    "tf-row",
    isFeatured ? "is-featured" : "",
    weak ? "is-weak" : "",
  ].filter(Boolean).join(" ");

  return (
    <div className={classes}>
      <div className="tf-cell tf-cell-window">
        <span className="tf-window-name">{row.window_name}</span>
        <span className="tf-window-sub">
          {row.window}-day window · {row.forecast_window}d out
        </span>
      </div>

      <div className="tf-cell tf-cell-call">
        <div className="tf-call-badges">
          {/* The call follows the numbers printed beside it: the strict set's direction
              when a published signal was disclosed, the readout pool's otherwise. */}
          <DirectionBadge direction={conviction.direction} />
          {isFeatured && (
            <Badge className="tf-featured-badge">
              Featured{row.featured_rank ? ` #${row.featured_rank}` : ""}
            </Badge>
          )}
        </div>
        <span className="tf-call-sub">
          {featuredGated
            // Withheld, not false — say so rather than claiming this window missed the list.
            ? <Link to={authUrlFor("/login")}>Log in to see today&apos;s top list</Link>
            : isFeatured ? "On today's top list" : "Readout only"}
        </span>
      </div>

      <div className="tf-cell tf-cell-conviction">
        <div
          className="tf-split"
          role="img"
          aria-label={`${conviction.fraction_up.toFixed(0)} percent of analogs rose, ${conviction.fraction_dn.toFixed(0)} percent fell`}
        >
          <span className="tf-split-up" style={{ width: `${conviction.fraction_up}%` }} />
          <span className="tf-split-dn" style={{ width: `${conviction.fraction_dn}%` }} />
        </div>
        {/* Denominator is explicit: "the N close analogs" (the published signal's strict
            set) vs "all N analogs" (the no-floor pool the quality cell counts). The two
            are different sizes for the same row, so neither may be printed as a bare N. */}
        <span className="tf-conviction-caption">
          <strong>{pct(conviction.fraction_up)}</strong>{" of "}
          {strictStats
            ? <>the {conviction.count_total} close analogs</>
            : <>all {conviction.count_total} analogs</>}
          {" rose · avg "}<span className="tf-up">{move(conviction.avg_up, 1)}</span>
          {" / "}<span className="tf-dn">{move(conviction.avg_dn, -1)}</span>
        </span>
        <span className="tf-basis-note">
          {strictStats
            ? "Same analogs as the published signal"
            : "Full analog pool — no similarity floor"}
        </span>
      </div>

      <div className="tf-cell tf-cell-quality">
        <div className="tf-quality-head" title={QUALITY_HINT[row.match_quality]}>
          <QualityMeter quality={row.match_quality} />
          <span className="tf-quality-label">{QUALITY_LABEL[row.match_quality]}</span>
        </div>
        <span className="tf-quality-sub">
          {row.count_close_matches} close of {row.count_analogs} analogs scanned
          {row.match_score_median !== null && <> · median sim {score(row.match_score_median)}</>}
        </span>
      </div>

      <div className="tf-cell tf-cell-action">
        {/* "unknown" quality means the matcher found zero analogs at any similarity for
            this ticker/window — no score would make a live query return something, so
            there is nothing useful to open rather than a link that can only 404 into
            "no matches found". */}
        {href === null
          ? <span className="tf-open tf-open-disabled">No analogs to open</span>
          : <Link className="tf-open" to={href}>{isFeatured ? "View signal" : "Open matcher"}</Link>}
      </div>
    </div>
  );
}

export default function TickerTimeframes(): React.JSX.Element {
  const { ticker: routeTicker } = useParams<{ ticker?: string }>();
  const navigate = useNavigate();
  const posthog = usePostHog();

  const ticker = (routeTicker ?? "").trim().toUpperCase();
  const [input, setInput] = useState(ticker);
  const [data, setData] = useState<TickerSignalsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useSeo({
    title: ticker ? `${ticker} across every timeframe` : "Every timeframe for one ticker",
    description: ticker
      ? `What DejaChart's historical analogs say about ${ticker} at 2 weeks, 4 weeks, 3 months, 6 months and 1 year — all in one view.`
      : "Look up one ticker and see what the historical analogs say at every timeframe at once.",
  });

  useEffect(() => { setInput(ticker); }, [ticker]);

  useEffect(() => {
    if (!ticker) { setData(null); setError(null); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    requests.get(`/api/ticker/${encodeURIComponent(ticker)}/signals`)
      .then(res => {
        if (!alive) return;
        setData(res.data as TickerSignalsResponse);
        setLoading(false);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        const status = (e as { response?: { status?: number } } | undefined)?.response?.status;
        setData(null);
        setError(status === 404 ? `We don't have data for ${ticker}.` : "Could not load this ticker right now.");
        setLoading(false);
      });
    return (): void => { alive = false; };
  }, [ticker]);

  useEffect(() => {
    if (ticker) posthog.capture("ticker_timeframes_viewed", { ticker });
  }, [ticker, posthog]);

  const submit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const next = input.trim().toUpperCase();
    if (next) navigate(`/ticker/${encodeURIComponent(next)}`);
  }, [input, navigate]);

  const rows = data?.windows ?? {};
  // Signed-out callers get no featured_* fields at all (the API withholds list positions).
  const featuredGated = data?.featured_gated === true;
  // A known ticker with nothing to say at any window is a valid answer, not an error.
  const allEmpty = !!data && WINDOWS.every(w => !rows[String(w)]);

  return (
    <Container className="py-4 ticker-timeframes">
      <h2 className="tf-title">Every timeframe, one ticker</h2>
      <p className="tf-lede">
        What the closest historical analogs did next — read at two weeks, four weeks, three
        months, six months and a year, side by side.
      </p>

      <form onSubmit={submit} className="tf-search">
        <InputGroup>
          <Input
            aria-label="Ticker symbol"
            placeholder="Enter a ticker (e.g. DELL)"
            value={input}
            onChange={e => setInput(e.target.value.toUpperCase())}
          />
          <Button color="primary" type="submit" disabled={!input.trim()}>
            Show all timeframes
          </Button>
        </InputGroup>
      </form>

      {!ticker && !loading && (
        <Card className="tf-placeholder">
          <CardBody>
            Enter a ticker above to see every window at once, or open one from{" "}
            <Link to="/featured">today&apos;s matches</Link>.
          </CardBody>
        </Card>
      )}

      {loading && (
        <div className="tf-loading"><Spinner color="primary" /></div>
      )}

      {error && !loading && <Alert color="warning" className="tf-alert">{error}</Alert>}

      {data && !loading && !error && (
        <>
          <div className="tf-header">
            <div>
              <span className="tf-header-ticker">{data.ticker}</span>
              {data.ticker_name && data.ticker_name !== data.ticker && (
                <span className="tf-header-name">{data.ticker_name}</span>
              )}
            </div>
            {data.date && <span className="tf-header-date">Data as of {data.date}</span>}
          </div>

          {allEmpty ? (
            <Alert color="secondary" className="tf-alert">
              {data.ticker} has no analog readout today at any window. That usually means too
              little price history, or a feed that has gone stale.
            </Alert>
          ) : (
            <>
              <div className="tf-rows">
                {WINDOWS.map(w => {
                  const row = rows[String(w)];
                  return row
                    ? (
                      <ReadoutRow
                        key={w}
                        ticker={data.ticker}
                        row={row}
                        featuredGated={featuredGated}
                      />
                    )
                    : <EmptyRow key={w} windowKey={w} />;
                })}
              </div>
              <p className="tf-footnote">
                Every window is shown, including ones built from loose analogs — the match
                meter says how close the typical analog actually is, so a loose row reads as
                loose. The two counts are different sets on purpose: the conviction figure
                counts the analogs behind that row&apos;s call, while &ldquo;of N analogs
                scanned&rdquo; counts the whole unfiltered pool the matcher returned.
                {featuredGated
                  ? " Log in to see which windows made today's published top list."
                  : " \u201cFeatured\u201d marks a window where this ticker made today's published top list; the rest are readouts of the same underlying history."}
              </p>
            </>
          )}
        </>
      )}
    </Container>
  );
}
